/**
 * `AgentChannelPlaceholder` — the honest empty/non-active panel for a channel
 * tab. Kept (per the C2 handoff: "keep it — it becomes the zero-state") and
 * reused for these states via optional key overrides:
 *
 *   - gate off (runtime predates `channels.*`) → defaults: "Em breve…";
 *   - connected but no activity → `agent.channels.zeroState.*`;
 *   - e-mail with no mailboxes → `agent.channels.email.noMailboxes`.
 *
 * The NOT-CONNECTED state no longer lands here for the IM tabs: it moved to
 * `AgentChannelConnectCta` when the tab gained a Connect button (operator
 * direction 2026-07-26). The `agent.channels.notConnected.*` keys remain a
 * supported (currently unused) override.
 *
 * An empty list would read as "your agent has done nothing on WhatsApp" — a lie
 * about the agent's activity, the worst failure for a monitoring surface — so
 * every not-active state says the honest thing instead.
 */
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";

export function AgentChannelPlaceholder({
  icon: Icon,
  channelKey,
  titleKey,
  bodyKey,
  testId,
}: {
  icon: LucideIcon;
  /** i18n suffix — `email` | `telegram` | `whatsapp`. Drives the default keys
   *  AND the default test id. */
  channelKey: string;
  /** Override the heading key (defaults to `agent.channels.<channelKey>.title`). */
  titleKey?: string;
  /** Override the body key (defaults to `agent.channels.<channelKey>.pending`). */
  bodyKey?: string;
  /** Override the test id (defaults to `agent-channel-placeholder-<channelKey>`). */
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid={testId ?? `agent-channel-placeholder-${channelKey}`}
      className="flex flex-col items-center justify-center rounded-lg border border-dashed border-cream-300 px-6 py-16 text-center dark:border-coffee-700"
    >
      <Icon size={28} className="text-cream-500 dark:text-coffee-600" aria-hidden />
      <h3 className="mt-4 font-serif-display text-lg text-coffee-900 dark:text-cream-100">
        {t(titleKey ?? `agent.channels.${channelKey}.title`)}
      </h3>
      <p className="mt-2 max-w-md text-body-sm text-coffee-600 dark:text-cream-400">
        {t(bodyKey ?? `agent.channels.${channelKey}.pending`)}
      </p>
      <p className="mt-3 max-w-md text-xs text-coffee-500 dark:text-cream-500">
        {t("agent.channels.readOnlyNote")}
      </p>
    </div>
  );
}
